import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import { PGLineChart, PGBarChart, PGPieChart } from '../../components/primitives/PGChart';
import { GROUP, BRANCHES, COUNTRIES, TRENDS, MONTHS } from '../../utils/orgData';
import { PG } from '../../utils/theme';
import { useLiveFeed } from '../../components/live/LiveFeedProvider';
import TickerNumber from '../../components/live/TickerNumber';
import SplitFlap from '../../components/live/SplitFlap';

const { FiDollarSign, FiTrendingUp, FiPercent, FiClock, FiInbox } = FiIcons;

const fmtR = (v) => 'R ' + Math.round(v).toLocaleString('en-ZA');

export default function FinanceSliver() {
  const f = GROUP.kpis.finance;

  const countryPie = COUNTRIES.map((c) => ({ name: c.name, value: c.kpis.finance.revenueMTD }));

  const roiTop = [...BRANCHES].sort((a, b) => b.finance.roiBranch - a.finance.roiBranch).slice(0, 10);

  const bottom5 = [...BRANCHES].sort((a, b) => a.finance.gpPct - b.finance.gpPct).slice(0, 5);

  const cashSum = BRANCHES.reduce((s, b) => s + b.finance.cashCollected, 0);
  const cashSpark = Array.from({ length: 7 }, (_, i) => Math.round(cashSum * (0.7 + i * 0.05 + (Math.sin(i) * 0.02))));

  const ageingData = [
    { bucket: 'Current', value: 62 },
    { bucket: '30 days', value: 22 },
    { bucket: '60 days', value: 11 },
    { bucket: '90+', value: 5 },
  ];

  const feed = useLiveFeed();
  const cashLive = Math.round(feed.revenueToday * 8.8);
  const revHr = feed.revenuePerHour[feed.revenuePerHour.length - 1] ?? 0;
  const insHr = Math.round(revHr * (feed.insuranceSplitPct / 100));
  const retHr = revHr - insHr;
  const insSplit = feed.insuranceSplitPct;
  const worstDebtors = [...BRANCHES]
    .map((b) => ({ name: b.name, days: b.finance.debtorsDays }))
    .sort((a, b) => b.days - a.days)
    .slice(0, 3);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="REVENUE MTD" value={`R ${(f.revenueMTD / 1e6).toFixed(1)}M`} icon={FiDollarSign} accent={PG.red} target="R 90M" trend="up" trendValue="+4.2%" animated="ticker" numericValue={cashLive} tickerDigits={9} tickerPrefix="R " />
        <KPICard title="GP%" value={f.gpPct} unit="%" icon={FiPercent} accent={PG.emerald} target=">42%" trend="up" trendValue="+0.8pp" />
        <KPICard title="EBITDA%" value={f.ebitdaPct} unit="%" icon={FiTrendingUp} accent={PG.emerald} target=">18%" trend="flat" trendValue="stable" />
        <KPICard title="DEBTORS DAYS" value={f.debtorsDays} unit="days" icon={FiClock} accent={PG.amber} target="<35" trend="down" trendValue="-2d" animated="splitflap" flapText={String(f.debtorsDays).padStart(2, '0')} flapWidth={2} />
        <KPICard title="CASH COLLECTED" value={`R ${(f.cashCollected / 1e6).toFixed(1)}M`} icon={FiInbox} accent={PG.cyan} trend="up" trendValue="today" sparkValues={feed.revenuePerHour} />
      </div>

      <div className="flex items-baseline gap-4 flex-shrink-0 px-4 py-3 border border-white/10 bg-[#0A0A0A]">
        <span className="font-mono-tech text-[10px] text-neutral-500 uppercase tracking-[0.25em]">
          Cash_Collected_Live //
        </span>
        <span className="font-doto text-3xl xl:text-4xl font-black text-[#00FF41] tracking-tighter">
          <TickerNumber value={cashLive} digits={9} prefix="R " />
        </span>
        <span className="font-mono-tech text-[9px] text-neutral-600 uppercase tracking-[0.2em] ml-auto">
          Updates every 2s
        </span>
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <div className="col-span-4 flex flex-col gap-2">
          <Panel title="REVENUE_BY_COUNTRY" subtitle="MTD share" className="flex-1 min-h-0">
            <div className="h-full p-2">
              <PGPieChart data={countryPie} />
            </div>
          </Panel>
          <div className="px-3 py-2 border border-white/10 bg-[#0A0A0A] flex justify-between items-center font-mono-tech text-[10px] text-neutral-400 uppercase tracking-wider flex-shrink-0">
            <span>INS R <TickerNumber value={insHr} digits={5} />/hr</span>
            <span>RETAIL R <TickerNumber value={retHr} digits={5} />/hr</span>
            <span className="text-[#FFB800]">INS_SPLIT <TickerNumber value={insSplit} digits={2} />/{100 - insSplit}</span>
          </div>
        </div>

        <Panel title="ROI_RANKING_BY_BRANCH" subtitle="top 10 branches" className="col-span-4">
          <div className="h-full p-2">
            <PGBarChart
              horizontal
              categories={roiTop.map((b) => b.name).reverse()}
              series={[
                {
                  name: 'ROI %',
                  data: roiTop.map((b) => ({ value: b.finance.roiBranch, itemStyle: { color: PG.emerald } })).reverse(),
                },
              ]}
              yFormatter={(v) => v + '%'}
            />
          </div>
        </Panel>

        <Panel title="12_MO_REVENUE" subtitle="group rolling (R millions)" className="col-span-4" livePulse>
          <div className="h-full p-2">
            <PGLineChart
              categories={MONTHS}
              series={[{ name: 'Revenue', data: TRENDS.groupRevenue.map((v) => +(v / 1e6).toFixed(1)), itemStyle: { color: PG.red }, lineStyle: { color: PG.red, width: 2 } }]}
              yFormatter={(v) => v + 'M'}
            />
          </div>
        </Panel>

        <Panel title="BOTTOM_5_GP%" subtitle="margin-watch" tone="alert" className="col-span-4">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {bottom5.map((b, i) => (
              <Link
                key={b.id}
                to={`/sliver/finance/branch/${b.id}`}
                className="flex items-center justify-between px-4 py-3 border-b border-white/5 hover:bg-[#D71921]/10 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="font-doto text-xl text-[#D71921] w-5">{i + 1}</span>
                  <div>
                    <div className="text-white text-sm font-medium">{b.name}</div>
                    <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">{b.country}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-doto text-2xl text-[#D71921]">{b.finance.gpPct}%</div>
                </div>
              </Link>
            ))}
          </div>
        </Panel>

        <Panel title="CASH_COLLECTION_TODAY" subtitle="group rolling 7d" className="col-span-4">
          <div className="h-full p-4 flex flex-col justify-between">
            <div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-2">Collected today</div>
              <div className="font-doto text-5xl text-white leading-none">{fmtR(cashSum)}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[#00C27A] mt-2">+6.4% vs yesterday</div>
            </div>
            <div className="h-20 -mx-2">
              <PGLineChart
                categories={['D-6','D-5','D-4','D-3','D-2','D-1','TDY']}
                series={[{ name: 'Cash', data: cashSpark, itemStyle: { color: PG.cyan }, lineStyle: { color: PG.cyan, width: 2 } }]}
                yFormatter={(v) => (v / 1e6).toFixed(1) + 'M'}
              />
            </div>
          </div>
        </Panel>

        <div className="col-span-4 flex flex-col gap-2">
          <Panel title="DEBTORS_AGEING" subtitle="% of book" className="flex-1 min-h-0">
            <div className="h-full p-2">
              <PGBarChart
                categories={ageingData.map((a) => a.bucket)}
                series={[
                  {
                    name: '% of book',
                    data: ageingData.map((a, i) => ({
                      value: a.value,
                      itemStyle: { color: [PG.emerald, PG.amber, PG.red, PG.red][i] },
                    })),
                  },
                ]}
                yFormatter={(v) => v + '%'}
              />
            </div>
          </Panel>
          <div className="mt-2 px-3 py-2 border border-white/10 bg-[#0A0A0A] font-mono-tech text-[9px] text-neutral-400 uppercase tracking-wider flex-shrink-0">
            <div className="text-neutral-600 mb-1 tracking-[0.2em]">TOP_3_OVERDUE:</div>
            {worstDebtors.map((b) => (
              <div key={b.name} className="flex justify-between items-center py-0.5">
                <SplitFlap text={b.name.toUpperCase()} width={10} />
                <span className="text-[#D71921]">
                  <SplitFlap text={String(b.days).padStart(2, '0')} width={2} /> days
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
